import { useCallback, useMemo, useState } from "react";
import { useBookings } from "../contexts/BookingsContext";
import { services } from "../data/services";
import { findNextAvailable, toDateKey, type SlotTakenCheck } from "../utils/slots";
import type { Appointment, Service } from "../types/booking";

export type BookingStep = "service" | "slot" | "details" | "done";

export function useBookingFlow(initialServiceId?: string | null) {
  const { addAppointment, isSlotTaken } = useBookings();
  const initialService = services.find((s) => s.id === initialServiceId) ?? null;

  const [serviceId, setServiceId] = useState<string | null>(initialService?.id ?? null);
  const [step, setStep] = useState<BookingStep>(initialService ? "slot" : "service");
  const [dateKey, setDateKey] = useState<string | null>(() =>
    initialService ? nextDateKey(initialService, isSlotTaken) : null
  );
  const [time, setTime] = useState<string | null>(null);
  const [hasPrescription, setHasPrescription] = useState(false);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState<Appointment | null>(null);

  const service = useMemo(() => services.find((s) => s.id === serviceId) ?? null, [serviceId]);

  const selectService = useCallback(
    (id: string) => {
      const next = services.find((s) => s.id === id);
      if (!next) return;
      setServiceId(id);
      setDateKey(nextDateKey(next, isSlotTaken));
      setTime(null);
      setHasPrescription(false);
      setError(null);
      setStep("slot");
    },
    [isSlotTaken]
  );

  const selectDate = useCallback((key: string) => {
    setDateKey(key);
    setTime(null);
  }, []);

  const canGoTo = useCallback(
    (target: BookingStep) => {
      if (target === "service") return true;
      if (target === "slot") return Boolean(service);
      if (target === "details") return Boolean(service && dateKey && time);
      return false;
    },
    [service, dateKey, time]
  );

  const goToStep = useCallback(
    (target: BookingStep) => {
      if (!canGoTo(target)) return;
      setError(null);
      setStep(target);
    },
    [canGoTo]
  );

  const canConfirm = Boolean(
    service && dateKey && time && (!service.requiresPrescription || hasPrescription)
  );

  const confirm = useCallback(async () => {
    if (!service || !dateKey || !time || !canConfirm) return;
    setSubmitting(true);
    setError(null);
    try {
      const appointment = await addAppointment({
        serviceId: service.id,
        dateKey,
        time,
        note: note.trim() || undefined,
      });
      setConfirmed(appointment);
      setStep("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "La réservation a échoué. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  }, [service, dateKey, time, canConfirm, note, addAppointment]);

  return {
    step,
    service,
    dateKey,
    time,
    hasPrescription,
    note,
    submitting,
    error,
    confirmed,
    canConfirm,
    canGoTo,
    selectService,
    selectDate,
    setTime,
    setHasPrescription,
    setNote,
    goToStep,
    confirm,
  };
}

function nextDateKey(service: Service, isTaken: SlotTakenCheck): string | null {
  const next = findNextAvailable(service.duration, isTaken);
  return next ? toDateKey(next.date) : null;
}
